import { z } from 'zod';

// Shared password regex: min 8 chars, at least 1 uppercase, 1 lowercase, 1 digit
export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
export const PASSWORD_RULES_MSG =
  'Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter, and one number';

// ============================================================================
// Step 1: Account (name, phone, role)
// ============================================================================
export const signupStep1Schema = z.object({
  first_name: z.string().min(1, 'First name is required').trim(),
  middle_name: z
    .string()
    .max(50, 'Middle name too long')
    .optional()
    .or(z.literal('')),
  last_name: z.string().min(1, 'Last name is required').trim(),
  phone_number: z
    .string()
    .min(1, 'Phone number is required')
    .refine((val) => val.replace(/\D/g, '').length === 10, {
      message: 'Please enter a valid 10-digit mobile number',
    }),
  country_code: z.string().min(1, 'Country code is required'),
  role: z.enum(['user', 'doctor']),
});

export type SignupStep1Form = z.infer<typeof signupStep1Schema>;

// ============================================================================
// Step 1 OTP verification
// ============================================================================
export const signupStep1OtpSchema = z.object({
  otp: z
    .string()
    .min(1, 'OTP is required')
    .refine((val) => val.replace(/\D/g, '').length === 6, {
      message: 'OTP should be 6 digits',
    }),
});

export type SignupStep1OtpForm = z.infer<typeof signupStep1OtpSchema>;

// ============================================================================
// Step 2: Credentials (email, password)
// ============================================================================
export const signupStep2Schema = z
  .object({
    email: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address'),
    password: z
      .string()
      .min(1, 'Password is required')
      .regex(PASSWORD_REGEX, PASSWORD_RULES_MSG),
    confirm_password: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

export type SignupStep2Form = z.infer<typeof signupStep2Schema>;

// ============================================================================
// Step 2: Skip verification (optional password when provided must be valid)
// ============================================================================
export const signupCredentialsSkipSchema = z
  .object({
    user_id: z.coerce.number().int().positive('User ID is required'),
    password: z.string().optional(),
    confirm_password: z.string().optional(),
  })
  .refine(
    (data) => {
      const hasPwd = !!data.password?.trim();
      const hasConfirm = !!data.confirm_password?.trim();
      if (hasPwd && !hasConfirm) return false;
      if (!hasPwd && hasConfirm) return false;
      return true;
    },
    {
      message: 'Please enter and confirm your password',
      path: ['confirm_password'],
    },
  )
  .refine(
    (data) => {
      const hasPwd = !!data.password?.trim();
      const hasConfirm = !!data.confirm_password?.trim();
      if (!hasPwd && !hasConfirm) return true;
      return (
        PASSWORD_REGEX.test(data.password!) &&
        data.password === data.confirm_password
      );
    },
    {
      message: PASSWORD_RULES_MSG + ' and match confirmation',
      path: ['password'],
    },
  );

export type SignupCredentialsSkipForm = z.infer<
  typeof signupCredentialsSkipSchema
>;

// ============================================================================
// Login (email + password)
// ============================================================================
export const loginSchema = z.object({
  credential: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'Please enter your password'),
});

export type LoginForm = z.infer<typeof loginSchema>;

// ============================================================================
// Email OTP verification (reusable for EmailVerificationModal, etc.)
// ============================================================================
export const emailOtpSchema = z.object({
  otp: z
    .string()
    .min(1, 'Please enter the verification code')
    .refine((val) => val.replace(/\D/g, '').length === 6, {
      message: 'Please enter a valid 6-digit code',
    }),
});

export type EmailOtpForm = z.infer<typeof emailOtpSchema>;

// ============================================================================
// Phone verification (same rules as Step 1 signup + doctor phone step)
// ============================================================================
export const phoneVerificationSchema = z.object({
  countryCode: z.string().min(1, 'Country code is required'),
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .refine((val) => val.replace(/\D/g, '').length === 10, {
      message: 'Please enter a valid 10-digit mobile number.',
    }),
});

export type PhoneVerificationForm = z.infer<typeof phoneVerificationSchema>;
